import React, { useEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
    X, Wallet, Gauge, ListChecks, Loader2, AlertTriangle, RefreshCw,
    User as UserIcon, Building2, Code2, CalendarRange,
} from 'lucide-react';
import { useAuth } from '../../../src/contexts/AuthContext';
import {
    getLemitSaldo, getLemitLog, getLemitUsageStats, getLemitConsultaRaw,
    LemitLogRow, LemitUsageStats,
} from '../../../src/services/lemit';
import { propsFundoModal } from '../../../src/lib/backdropModal';

const formatDocumento = (doc?: string): string => {
    const c = (doc || '').replace(/\D/g, '');
    if (c.length === 11) return c.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    if (c.length === 14) return c.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
    return doc || '—';
};
const fmtBRL = (v?: number | null) => v != null ? v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) : '—';
const fmtDateTime = (iso?: string) => iso ? new Date(iso).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

type Period = 'mes' | '30d' | 'tudo';
const periodStart = (p: Period): string | undefined => {
    const now = new Date();
    if (p === 'mes') return new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
    if (p === '30d') return new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString();
    return undefined;
};

interface Props {
    isOpen: boolean;
    onClose: () => void;
}

const card = 'bg-white dark:bg-slate-900/60 border border-slate-200/80 dark:border-slate-800 rounded-2xl shadow-sm';

const LemitCentralCustosModal: React.FC<Props> = ({ isOpen, onClose }) => {
    const { profile } = useAuth();

    const [period, setPeriod] = useState<Period>('mes');
    const [saldo, setSaldo] = useState<any>(null);
    const [saldoLoading, setSaldoLoading] = useState(false);
    const [saldoError, setSaldoError] = useState<string | null>(null);
    const [stats, setStats] = useState<LemitUsageStats | null>(null);
    const [log, setLog] = useState<LemitLogRow[]>([]);
    const [logLoading, setLogLoading] = useState(false);
    const [expanded, setExpanded] = useState<number | null>(null);
    const [rawCache, setRawCache] = useState<Record<number, any>>({});

    const loadSaldo = useCallback(async () => {
        setSaldoLoading(true); setSaldoError(null);
        try {
            setSaldo(await getLemitSaldo());
        } catch (e: any) {
            setSaldoError(e.message || 'Não foi possível obter o saldo.');
        } finally {
            setSaldoLoading(false);
        }
    }, []);

    const loadUsage = useCallback(async (p: Period) => {
        setLogLoading(true);
        const from = periodStart(p);
        const [s, rows] = await Promise.all([getLemitUsageStats(from), getLemitLog({ from, limit: 200 })]);
        setStats(s);
        setLog(rows);
        setLogLoading(false);
    }, []);

    useEffect(() => {
        if (!isOpen) return;
        setExpanded(null); setRawCache({});
        loadSaldo();
        loadUsage(period);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    useEffect(() => {
        if (isOpen) loadUsage(period);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [period]);

    const toggleRaw = async (id: number) => {
        if (expanded === id) { setExpanded(null); return; }
        setExpanded(id);
        if (!rawCache[id]) {
            const raw = await getLemitConsultaRaw(id);
            setRawCache((c) => ({ ...c, [id]: raw }));
        }
    };

    if (!isOpen) return null;

    // Render genérico do saldo (formato do endpoint /saldo não é fixo na doc)
    const renderSaldo = () => {
        if (saldoLoading) return <div className="flex items-center gap-2 text-sm text-slate-500 py-4"><Loader2 size={16} className="animate-spin" /> Carregando saldo...</div>;
        if (saldoError) return (
            <div className="flex items-start gap-2 px-3 py-3 rounded-xl bg-amber-50 dark:bg-amber-900/10 text-amber-700 dark:text-amber-400 border border-amber-200 dark:border-amber-800/40 text-xs">
                <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />
                <div>{saldoError}<br /><span className="text-amber-600/70">Verifique se o secret LEMIT_API_TOKEN está configurado no Supabase.</span></div>
            </div>
        );
        if (!saldo) return <p className="text-xs text-slate-400 py-2">Sem dados de saldo.</p>;
        const entries = saldo && typeof saldo === 'object' && !Array.isArray(saldo) ? Object.entries(saldo) : [];
        const scalars = entries.filter(([, v]) => v == null || typeof v !== 'object');
        return (
            <div className="space-y-3">
                {scalars.length > 0 && (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                        {scalars.map(([k, v]) => (
                            <div key={k} className="px-3 py-2.5 bg-slate-50 dark:bg-slate-800/60 rounded-xl border border-slate-200/60 dark:border-slate-700">
                                <p className="text-[10px] font-extrabold uppercase tracking-wider text-slate-400">{k.replace(/_/g, ' ')}</p>
                                <p className="text-sm font-bold text-slate-700 dark:text-slate-200">{String(v ?? '—')}</p>
                            </div>
                        ))}
                    </div>
                )}
                <details className="text-[11px]">
                    <summary className="cursor-pointer text-slate-400 font-semibold">Ver retorno completo (JSON)</summary>
                    <pre className="mt-2 p-3 bg-slate-50 dark:bg-slate-800/60 rounded-xl overflow-x-auto max-h-48 text-slate-600 dark:text-slate-300">{JSON.stringify(saldo, null, 2)}</pre>
                </details>
            </div>
        );
    };

    return createPortal(
        <div className="fixed inset-0 z-[120] bg-black/60 backdrop-blur-sm flex items-center justify-center p-3 sm:p-6 animate-fade-in" {...propsFundoModal(() => onClose())}>
            <div className="bg-gray-50 dark:bg-slate-950 rounded-2xl w-full max-w-5xl max-h-[92vh] flex flex-col shadow-2xl overflow-hidden" onClick={e => e.stopPropagation()}>

                {/* Header */}
                <div className="px-5 sm:px-6 py-4 bg-gradient-to-r from-slate-800 to-slate-900 text-white flex items-center justify-between flex-shrink-0">
                    <div className="flex items-center gap-3">
                        <div className="p-2.5 bg-white/10 rounded-xl"><Wallet size={20} /></div>
                        <div>
                            <h2 className="text-lg font-extrabold leading-tight">Central de Custos · API Lemit</h2>
                            <p className="text-[11px] text-white/70 font-medium">Saldo, consumo e registro completo de consultas</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-full transition-colors"><X size={20} /></button>
                </div>

                <div className="flex-1 overflow-y-auto p-4 sm:p-5 space-y-4">

                    {/* Saldo ao vivo */}
                    <div className={card}>
                        <div className="flex items-center gap-2.5 px-5 py-3.5 border-b border-slate-100 dark:border-slate-800">
                            <div className="p-1.5 rounded-lg bg-emerald-50 dark:bg-emerald-900/15 text-emerald-700 dark:text-emerald-400"><Gauge size={14} /></div>
                            <h4 className="text-sm font-bold text-slate-700 dark:text-slate-200">Saldo e consumo (ao vivo)</h4>
                            <button onClick={loadSaldo} disabled={saldoLoading} className="ml-auto p-1.5 rounded-lg text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-50" title="Atualizar saldo">
                                <RefreshCw size={14} className={saldoLoading ? 'animate-spin' : ''} />
                            </button>
                        </div>
                        <div className="p-4">{renderSaldo()}</div>
                    </div>

                    {/* Período + estatísticas */}
                    <div className={`${card} p-4`}>
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                            <div className="flex items-center gap-2">
                                <CalendarRange size={15} className="text-slate-400" />
                                <div className="flex items-center p-1 bg-gray-100 dark:bg-slate-800 rounded-full">
                                    {([['mes', 'Este mês'], ['30d', 'Últimos 30 dias'], ['tudo', 'Tudo']] as [Period, string][]).map(([val, label]) => (
                                        <button
                                            key={val}
                                            onClick={() => setPeriod(val)}
                                            className={`px-3 py-1.5 rounded-full text-xs font-bold transition-all ${period === val ? 'bg-white dark:bg-slate-700 text-primary shadow-sm' : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'}`}
                                        >{label}</button>
                                    ))}
                                </div>
                            </div>
                            <div className="flex items-center gap-3">
                                <div className="text-right">
                                    <p className="text-[10px] font-extrabold uppercase tracking-wider text-slate-400">Consultas</p>
                                    <p className="text-xl font-black text-slate-800 dark:text-slate-100 tabular-nums">{stats?.count ?? '—'}</p>
                                </div>
                                <div className="w-px h-8 bg-slate-200 dark:bg-slate-700" />
                                <div className="text-right">
                                    <p className="text-[10px] font-extrabold uppercase tracking-wider text-slate-400">Custo estimado</p>
                                    <p className="text-xl font-black text-[#5F6C37] dark:text-emerald-400 tabular-nums">
                                        {stats ? (stats.custoConhecido ? fmtBRL(stats.custoTotal) : '—') : '—'}
                                    </p>
                                </div>
                            </div>
                        </div>
                        {stats && !stats.custoConhecido && stats.count > 0 && (
                            <p className="text-[11px] text-slate-400 mt-2">Custo/consulta não configurado. Defina <b>lemit_custo_pessoa</b> / <b>lemit_custo_empresa</b> em Config &gt; APIs para estimar o gasto.</p>
                        )}
                    </div>

                    {/* Log completo */}
                    <div className={card}>
                        <div className="flex items-center gap-2.5 px-5 py-3.5 border-b border-slate-100 dark:border-slate-800">
                            <div className="p-1.5 rounded-lg bg-primary/10 text-primary"><ListChecks size={14} /></div>
                            <h4 className="text-sm font-bold text-slate-700 dark:text-slate-200">Registro de consultas</h4>
                            <span className="ml-auto text-[11px] font-semibold text-slate-400">{log.length} registro(s)</span>
                        </div>

                        {logLoading ? (
                            <div className="flex items-center justify-center py-10 gap-2 text-slate-500"><Loader2 size={18} className="animate-spin" /> Carregando...</div>
                        ) : log.length === 0 ? (
                            <p className="text-center text-sm text-slate-400 py-10">Nenhuma consulta no período.</p>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full text-xs">
                                    <thead>
                                        <tr className="border-b border-slate-100 dark:border-slate-700/50 text-left text-[10px] uppercase text-slate-400 font-extrabold">
                                            <th className="px-4 py-2.5">Quando</th>
                                            <th className="px-4 py-2.5">Quem</th>
                                            <th className="px-4 py-2.5">Documento</th>
                                            <th className="px-4 py-2.5">Tipo</th>
                                            <th className="px-4 py-2.5">Lead</th>
                                            <th className="px-4 py-2.5">Origem</th>
                                            <th className="px-4 py-2.5 text-right">Custo</th>
                                            <th className="px-4 py-2.5"></th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-50 dark:divide-slate-800">
                                        {log.map((row) => (
                                            <React.Fragment key={row.id}>
                                                <tr className="hover:bg-slate-50 dark:hover:bg-slate-800/40">
                                                    <td className="px-4 py-2.5 text-slate-500 whitespace-nowrap">{fmtDateTime(row.created_at)}</td>
                                                    <td className="px-4 py-2.5 font-semibold text-slate-700 dark:text-slate-200 whitespace-nowrap">{row.consultado_por_nome || '—'}</td>
                                                    <td className="px-4 py-2.5 font-mono text-slate-600 dark:text-slate-300 whitespace-nowrap">{formatDocumento(row.documento)}</td>
                                                    <td className="px-4 py-2.5">
                                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300">
                                                            {row.tipo === 'empresa' ? <Building2 size={10} /> : <UserIcon size={10} />}{row.tipo}
                                                        </span>
                                                    </td>
                                                    <td className="px-4 py-2.5 text-slate-600 dark:text-slate-300 max-w-[180px] truncate" title={row.negociacoes?.titulo || row.negociacoes?.cliente_nome || ''}>
                                                        {row.negociacoes?.titulo || row.negociacoes?.cliente_nome || (row.negociacao_id ? `#${row.negociacao_id}` : '—')}
                                                    </td>
                                                    <td className="px-4 py-2.5">
                                                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${row.source === 'cache' ? 'bg-sky-100 text-sky-700 dark:bg-sky-900/20 dark:text-sky-400' : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-400'}`}>
                                                            {row.source === 'cache' ? 'cache' : 'API'}
                                                        </span>
                                                    </td>
                                                    <td className="px-4 py-2.5 text-right tabular-nums text-slate-600 dark:text-slate-300">{row.custo_estimado != null ? fmtBRL(row.custo_estimado) : '—'}</td>
                                                    <td className="px-4 py-2.5 text-right">
                                                        <button onClick={() => toggleRaw(row.id)} className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800" title="Ver JSON retornado">
                                                            <Code2 size={13} />
                                                        </button>
                                                    </td>
                                                </tr>
                                                {expanded === row.id && (
                                                    <tr>
                                                        <td colSpan={8} className="px-4 pb-3 bg-slate-50/50 dark:bg-slate-800/20">
                                                            <pre className="p-3 bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 overflow-x-auto max-h-72 overflow-y-auto text-[11px] text-slate-600 dark:text-slate-300">
                                                                {rawCache[row.id] ? JSON.stringify(rawCache[row.id], null, 2) : 'Carregando...'}
                                                            </pre>
                                                        </td>
                                                    </tr>
                                                )}
                                            </React.Fragment>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>,
        document.body,
    );
};

export default LemitCentralCustosModal;
